import React from 'react';
import { Switch } from '../../../components/Switch';
import type { BaseInputProps } from '../../types/inputs';

export interface SwitchInputProps extends BaseInputProps<boolean> {
  /** Volitelný popisek vedle přepínače. */
  switchLabel?: string;
  className?: string;
}

/**
 * Atomický přepínač (toggle) pro formuláře — tenká nadstavba nad sdílenou
 * komponentou {@link Switch}, která drží vzhled i chování. Zde přibývá jen
 * napojení na `react-hook-form` přes {@link BaseInputProps}.
 *
 * Přístupné jméno je deterministické přes `aria-labelledby` (stejný vzor jako
 * {@link CheckboxInput} — FormField popisek + volitelný vnitřní popisek).
 */
export const SwitchInput: React.FC<SwitchInputProps> = ({
  name,
  id,
  value,
  onChange,
  onBlur,
  ref,
  disabled,
  readOnly,
  hasError,
  describedBy,
  labelId,
  testAttributes,
  switchLabel,
  className,
}) => {
  const checked = !!value;
  const innerLabelId = `${id}-inner-label`;
  const ids = React.useMemo(
    () => ({ hiddenInput: id, label: innerLabelId }),
    [id, innerLabelId],
  );

  const hiddenInputProps = React.useMemo(
    () => ({
      ref,
      name,
      'aria-labelledby': switchLabel ? `${labelId} ${innerLabelId}` : labelId,
      'aria-describedby': describedBy,
      ...testAttributes,
    }),
    [
      ref,
      name,
      switchLabel,
      labelId,
      innerLabelId,
      describedBy,
      testAttributes,
    ],
  );

  return (
    <Switch
      checked={checked}
      onCheckedChange={onChange}
      label={switchLabel}
      disabled={disabled}
      readOnly={readOnly}
      invalid={hasError}
      onBlur={onBlur}
      className={className}
      ids={ids}
      hiddenInputProps={hiddenInputProps}
    />
  );
};

SwitchInput.displayName = 'SwitchInput';
